import { ChevronDown, Search, SlidersHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { fitParts } from "./logos";
import { decisionByDate, planFor } from "../apply/recommend";

export function Choice({ label, value, options, onChange, testId, className = "" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={testId}
        className={`flex w-full items-center justify-between gap-3 rounded-full px-6 py-3 text-left outline-none transition-colors hover:bg-black/[0.05] ${className}`}>
        <span className="min-w-0">
          <span className="block whitespace-nowrap text-xs font-semibold text-foreground">{label}</span>
          <span className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-body">{value}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 w-64 overflow-y-auto rounded-card p-2 shadow-card">
        {options.map((o) => {
          const opt = typeof o === "string" ? { value: o, label: o } : o;
          return <DropdownMenuItem key={opt.value} onClick={() => onChange(opt.value)} className="cursor-pointer gap-2 rounded-xl px-3 py-2.5 text-sm">{opt.icon}{opt.label}</DropdownMenuItem>;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SearchButton({ onClick, className = "", pill = false }) {
  if (pill) return (
    <button type="button" onClick={onClick} data-testid="category-search-btn" aria-label="Filters" title="Filters"
      className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-gray5 bg-background px-3 text-sm font-medium text-primary shadow-sm transition-[background-color,border-color,box-shadow] duration-200 hover:bg-gray6 hover:shadow-card sm:px-4 ${className}`}>
      <SlidersHorizontal className="h-4 w-4" strokeWidth={2} /><span className="hidden md:inline">Filters</span>
    </button>
  );
  return (
    <button type="button" onClick={onClick} data-testid="category-search-btn" aria-label="Search visas" title="Search visas"
      className={`grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground shadow-card transition-[background-color,box-shadow,transform] hover:bg-[#0c1a33] hover:shadow-pop active:scale-[0.95] ${className}`}>
      <Search className="h-[18px] w-[18px]" strokeWidth={2.2} />
    </button>
  );
}

export function Chip({ on, children, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex h-9 shrink-0 items-center rounded-full border px-4 text-sm transition-colors ${on ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>
      {children}
    </button>
  );
}

export function IconTile({ icon: Icon, size = "md", className = "" }) {
  const s = size === "lg" ? "h-14 w-14" : "h-11 w-11";
  return <span className={`icon-tile shrink-0 ${s} ${className}`}><Icon className={size === "lg" ? "h-6 w-6" : "h-5 w-5"} strokeWidth={1.7} /></span>;
}

export function decisionDate(d, planId = "concierge") {
  const dt = decisionByDate({ ...d, tl: d.tl || d.timelines?.tourist || null }, planFor(planId));
  return dt ? dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;
}

export function StatusPill({ tone = "success", children, testId }) {
  const map = {
    success: "bg-[#E9F9EE] text-success-strong",
    warning: "bg-[#FFF1E5] text-warning-strong",
    danger: "bg-[#FFE9EA] text-danger",
    tint: "bg-accent text-tint-strong",
    neutral: "bg-gray6 text-label2",
  };
  const text = typeof children === "string" ? children : undefined;
  return <span data-testid={testId} data-pill title={text} className={`inline-flex h-7 max-w-full items-center gap-2 whitespace-nowrap rounded-full pl-3 pr-3.5 text-xs font-medium leading-none tracking-[-0.005em] ${map[tone]}`}><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80" /><span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap">{typeof children === "string" ? fitParts(children, 36) : children}</span></span>;
}

export function DecisionBadge({ d, planId = "concierge" }) {
  const r = d.rules || {};
  if (r.filing === "none") return <StatusPill tone="success">No visa needed</StatusPill>;
  if (r.filing === "form") return <StatusPill tone="success">{r.form_name || "Arrival form"} only · no decision</StatusPill>;
  if (d.tl?.same_day || (!d.tl && d.timelines?.tourist?.same_day)) return <StatusPill tone="success">Same day once filed</StatusPill>;
  const date = decisionDate(d, planId);
  return date ? <StatusPill tone="success">Decision expected by {date}</StatusPill> : <StatusPill tone="warning">{d.mode === "embassy" ? "Date set at interview" : "Timing varies"}</StatusPill>;
}

export function Segmented({ value, onChange, options, testId, size = "md" }) {
  return (
    <div role="tablist" data-testid={testId} className={`inline-flex rounded-xl bg-gray6 p-1 ${size === "lg" ? "text-sm" : "text-sm"}`}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" role="tab" aria-selected={on} onClick={() => onChange(o.value)} data-testid={`${testId}-${o.value}`}
            className={`relative inline-flex items-center rounded-[9px] px-4 font-semibold transition-[background-color,color,box-shadow] duration-200 ${size === "lg" ? "h-10" : "h-7"} ${on ? "bg-background text-foreground shadow-card" : "text-label2 hover:text-foreground"}`}>
            {o.label}{o.badge && <span className="ml-2 rounded-full bg-[#E9F9EE] px-1.5 py-0.5 text-xs font-medium text-success-strong">{o.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}
